import {
  FieldLabel,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import { spacing } from 'ext-ui/style/spacing'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { InvitePeopleScreen } from 'src/app/InvitePeople/InvitePeople'
import { selectNumberOfOrgMembersFromState } from 'src/app/OrganizationInfo/redux/organizationInfo.selectors' //
import { getOrgMembers } from 'src/app/PeopleMembers/redux/peopleMembers.actions'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgExpand from 'src/components/Svgs/SvgExpand'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { invitePeopleUrl, peopleMembersUrl } from 'src/navigation/urls'
import { putOrganization } from 'src/redux/actions/data/organizations.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

interface OrgMemberDetailsBoxProps {
  user: UserListModel
  organization: OrganizationsListModel
}

const OrgMemberDetailsBox: React.FC<OrgMemberDetailsBoxProps> = (
  props,
): React.ReactElement => {
  const numberOfOrgMembers = useSelector(selectNumberOfOrgMembersFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  const handleMemberAnalytics = (): void => {
    analytics?.track(
      // this stuff is inaccurate - screen is wrong (no longer have manage org flyout), intent is opening member list (not when when link to member list is pressed)
      'manage org flyout - view org member - text link press',
      {
        screen: `/manageOrg/${props.organization?.id}`,
        interaction: 'text link press',
        intent: 'open org member',
        user: props.user.id,
      },
    )
  }

  const dispatch = useBackgroundDispatch()
  useEffect((): void => {
    if (props.organization) {
      dispatch(
        getOrgMembers(props.organization.id, {
          showDeactivatedUsers: false,
          page: 0,
          count: 10,
          renderMetrics: false,
        }),
      )
    }
  }, [])

  return (
    <Box
      noShadow
      outlineBorder
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          margin-bottom: ${spacing(7)};
          display: flex;
          flex-direction: row;
          align-items: center;
        `}
      >
        <span
          css={css`
            flex-grow: 1;
            margin-right: 20px;
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate('Member Details')}
        </span>
        {props.user?.hasOrgAdminPermissions && (
          <span>
            <HoverableLink
              css={css`
                display: flex;
                align-items: center;
              `}
              to={{
                pathname: invitePeopleUrl(),
                search: '',
                state: {
                  screen: InvitePeopleScreen.Org,
                },
                hash: '',
              }}
              ariaLabelSource={'Invite to org'}
            >
              {translate('Invite to org')}
              <SvgExpand
                css={css`
                  transform: scale(0.7);
                  margin-left: 4px;
                `}
              />
            </HoverableLink>
          </span>
        )}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          &:not(:last-child) {
            margin-bottom: 20px;
          }
        `}
      >
        <FieldLabel>{translate('Member Count')}</FieldLabel>
        <HoverableLink
          size={HoverableLinkSizes.Large}
          underline
          regular
          to={peopleMembersUrl()}
          ariaLabelSource={`${numberOfOrgMembers} Active`}
          onClick={(): void => handleMemberAnalytics()}
        >
          {translate(
            '${number} Active ' +
              (numberOfOrgMembers === 1 ? 'Member' : 'Members'),
            {
              number: numberOfOrgMembers.toString(),
            },
          )}
        </HoverableLink>
        {props.user?.hasOrgAdminPermissions && (
          <div
            className={'flex-row align-center gap-3'}
            css={css`
              margin-top: 40px;
            `}
          >
            <Checkbox
              className={'display-members-to-all-org-members'}
              aria-label={translate('Display member list to all org members')}
              onChange={(): void => {
                if (organization !== undefined) {
                  dispatch(
                    putOrganization(organization.id, {
                      memberListVisible: !organization.memberListVisible,
                    }),
                  )
                  analytics?.track('org info display members', {
                    enabled: !organization?.memberListVisible,
                  })
                }
              }}
              checked={organization?.memberListVisible ?? false}
              data-is-checked={organization?.memberListVisible ?? false}
              /* organization?.memberListVisible is false when checkbox is selected
                and true, when checkbox is unselected, need to find out why
              */
              data-testid={`enableReceiptRules`}
            />
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                display: flex;
                align-items: center;
              `}
            >
              {translate('Display member list to all org members')}
              <Tooltip openLeft={false}>
                {translate(
                  'When checked, Members will be able to view other org members in the People Section. When unchecked, Members will only be able to view their Contacts.',
                )}
              </Tooltip>
            </span>
          </div>
        )}
      </div>
    </Box>
  )
}

export default OrgMemberDetailsBox
